import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCloudStatus } from "@/hooks/queries/use-cloud";
import { clearGcpWizardState, useGcpWizard } from "./wizard-state";

const usd = (n: number): string => `$${n.toFixed(2)}`;

/**
 * Step 6: the connected project, its run.app address and this month's spend against the budget.
 * Members (canManage false) get the same facts read-only, with no actions.
 */
export function LiveStep() {
  const { state } = useGcpWizard();
  const { data, isLoading } = useCloudStatus();
  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const gcp = data.connections.filter((c) => c.provider === "gcp");
  const c = gcp.find((x) => x.projectId === state.projectId) ?? gcp[0];
  if (!c) {
    return <p className="text-sm text-muted-foreground">Google Cloud isn't connected yet.</p>;
  }

  const over = c.budgetUsd != null && c.monthSpendUsd != null && c.monthSpendUsd >= c.budgetUsd;
  const near = !over && c.budgetUsd != null && c.monthSpendUsd != null && c.monthSpendUsd >= c.budgetUsd * 0.8;
  const spend =
    c.monthSpendUsd != null && c.budgetUsd != null
      ? `${usd(c.monthSpendUsd)} of ${usd(c.budgetUsd)} budget this month`
      : c.budgetUsd != null
        ? `Budget ${usd(c.budgetUsd)} a month · spend not read yet`
        : "Spend not read yet";
  const domainUrl = `https://console.cloud.google.com/run/domains?project=${encodeURIComponent(c.projectId ?? "")}`;

  return (
    <div className="space-y-4" aria-label="Live state">
      <h2 className="text-lg font-semibold">Connected</h2>
      <p className="text-sm text-muted-foreground">
        <strong className="text-foreground">{c.name}</strong> is connected. Projects can deploy to it.
      </p>
      <p className="text-sm">
        {c.liveUrl ? (
          <>
            Live at{" "}
            <a href={c.liveUrl} target="_blank" rel="noreferrer" className="underline">
              {c.liveUrl.replace(/^https?:\/\//, "")}
            </a>
          </>
        ) : (
          "No app deployed yet."
        )}
      </p>
      <p className={over ? "text-sm text-destructive" : near ? "text-sm text-amber-500" : "text-sm text-muted-foreground"}>{spend}</p>
      {data.canManage ? (
        <div className="flex flex-wrap gap-2">
          {c.liveUrl && (
            <Button asChild>
              <a href={c.liveUrl} target="_blank" rel="noreferrer">
                Open the app
              </a>
            </Button>
          )}
          <Button asChild variant="outline">
            <a href={domainUrl} target="_blank" rel="noreferrer">
              Add my domain
            </a>
          </Button>
          <Button asChild variant="ghost" onClick={() => clearGcpWizardState()}>
            <Link to="/connections">Go to Connections</Link>
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Only organization admins can change this connection.</p>
      )}
    </div>
  );
}
